'use client';

import {
    type MouseEvent,
    type ReactElement,
    type ReactNode,
    type TransitionEvent,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { SvgClose } from '@/components/icons-eataly';

/** Stable id for `aria-controls` on the menu trigger and the portaled dialog. */
export const EATALY_FULLSCREEN_NAV_OVERLAY_ID = 'eataly-fullscreen-nav-overlay';

const OVERLAY_MOTION_TIMING = 'duration-300 ease-in-out motion-reduce:transition-none motion-reduce:duration-0';

const MENU_TRIGGER_SELECTOR = '[data-testid="eataly-header-menu-trigger"]';

function usePrefersReducedMotion(): boolean {
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        const update = () => setPrefersReducedMotion(mediaQuery.matches);
        update();
        mediaQuery.addEventListener('change', update);
        return () => mediaQuery.removeEventListener('change', update);
    }, []);

    return prefersReducedMotion;
}

function getVisibleMenuTrigger(): HTMLElement | null {
    const triggers = document.querySelectorAll<HTMLElement>(MENU_TRIGGER_SELECTOR);
    for (const trigger of triggers) {
        if (trigger.offsetParent !== null) {
            return trigger;
        }
    }
    return triggers[0] ?? null;
}

function canRestoreFocus(element: HTMLElement | null): element is HTMLElement {
    if (!element?.isConnected) {
        return false;
    }
    return !element.closest('[data-testid="eataly-fullscreen-nav"]');
}

function restoreFocusAfterClose(previous: HTMLElement | null): void {
    if (canRestoreFocus(previous)) {
        previous.focus({ preventScroll: true });
        return;
    }
    getVisibleMenuTrigger()?.focus({ preventScroll: true });
}

function getFocusableElements(container: HTMLElement): HTMLElement[] {
    const selector =
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter((el) => {
        if (el.closest('[aria-hidden="true"]')) return false;
        const style = window.getComputedStyle(el);
        if (style.visibility === 'hidden' || style.display === 'none') return false;
        return el.offsetParent !== null || el.getClientRects().length > 0;
    });
}

export type EatalyFullscreenNavOverlayProps = {
    open: boolean;
    onClose: () => void;
    /** Called after the exit transition finishes and the overlay is unmounted. */
    onExitComplete?: () => void;
    isEditorialMenu?: boolean;
    children?: ReactNode;
    className?: string;
};

/**
 * Full-viewport overlay below the sticky header (--header-height). Header stays visible so the
 * menu trigger can switch between Menu and Close.
 */
export default function EatalyFullscreenNavOverlay({
    open,
    onClose,
    onExitComplete,
    isEditorialMenu = false,
    children,
    className,
}: EatalyFullscreenNavOverlayProps): ReactElement | null {
    const { t } = useTranslation('header');
    const prefersReducedMotion = usePrefersReducedMotion();
    const [mounted, setMounted] = useState(false);
    const [present, setPresent] = useState(false);
    const [entered, setEntered] = useState(false);
    const dialogRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);
    const wasVisibleRef = useRef(false);

    /** Drives slide transform/opacity — stays true during exit until transition completes. */
    const isPanelVisible = entered;

    useEffect(() => {
        setMounted(true);
    }, []);

    useLayoutEffect(() => {
        if (open) {
            setPresent(true);
            if (prefersReducedMotion) {
                setEntered(true);
                return;
            }
            setEntered(false);
            const frame = requestAnimationFrame(() => {
                requestAnimationFrame(() => setEntered(true));
            });
            return () => cancelAnimationFrame(frame);
        }

        if (prefersReducedMotion) {
            setEntered(false);
            setPresent(false);
            return;
        }

        // Begin exit slide; keep `present` until transform transitionend (or safety timeout).
        setEntered(false);
    }, [open, prefersReducedMotion]);

    const finishExit = useCallback(() => {
        setPresent(false);
        setEntered(false);
    }, []);

    useEffect(() => {
        if (open || !present) return;
        const safetyTimeout = window.setTimeout(finishExit, 400);
        return () => window.clearTimeout(safetyTimeout);
    }, [open, present, finishExit]);

    useEffect(() => {
        if (!open && !present) return;
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previous;
        };
    }, [open, present]);

    const trapTabNavigation = useCallback((e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;
        const container = dialogRef.current;
        if (!container) return;
        const active = document.activeElement;
        if (!active || !container.contains(active)) return;

        const nodes = getFocusableElements(container);
        if (nodes.length === 0) {
            e.preventDefault();
            container.focus();
            return;
        }
        if (nodes.length === 1) {
            e.preventDefault();
            nodes[0].focus();
            return;
        }
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey) {
            if (active === first) {
                e.preventDefault();
                last.focus();
            }
        } else if (active === last) {
            e.preventDefault();
            first.focus();
        }
    }, []);

    useEffect(() => {
        if (!open) return;
        const container = dialogRef.current;
        if (!container) return;

        const active = document.activeElement;
        previouslyFocusedRef.current = active instanceof HTMLElement ? active : null;

        const closeButton = closeButtonRef.current;
        if (closeButton) {
            closeButton.focus({ preventScroll: true });
        } else {
            const nodes = getFocusableElements(container);
            if (nodes.length > 0) {
                nodes[0].focus({ preventScroll: true });
            } else {
                container.focus({ preventScroll: true });
            }
        }

        document.addEventListener('keydown', trapTabNavigation, true);
        return () => {
            document.removeEventListener('keydown', trapTabNavigation, true);
        };
    }, [open, trapTabNavigation]);

    useEffect(() => {
        if (present || open) {
            wasVisibleRef.current = true;
            return;
        }
        if (!wasVisibleRef.current) {
            return;
        }
        wasVisibleRef.current = false;

        const restoreTarget = previouslyFocusedRef.current;
        previouslyFocusedRef.current = null;
        restoreFocusAfterClose(restoreTarget);

        onExitComplete?.();
    }, [present, open, onExitComplete]);

    const handleBackdropClick = useCallback(() => {
        onClose();
    }, [onClose]);

    const handleDialogClick = useCallback((e: MouseEvent) => {
        e.stopPropagation();
    }, []);

    const handlePanelTransitionEnd = useCallback(
        (event: TransitionEvent<HTMLDivElement>) => {
            if (event.target !== event.currentTarget) return;
            if (event.propertyName !== 'transform') return;
            if (!open && !entered) {
                finishExit();
            }
        },
        [open, entered, finishExit]
    );

    const isRendered = present || open;

    if (!mounted || !isRendered || typeof document === 'undefined') {
        return null;
    }

    const overlayTopStyle = { top: 0 };

    return createPortal(
        <>
            <div
                aria-hidden
                data-testid="eataly-fullscreen-nav-backdrop"
                className={cn(
                    'fixed inset-x-0 bottom-0 z-99 bg-foreground transition-opacity',
                    OVERLAY_MOTION_TIMING,
                    isPanelVisible ? 'opacity-90' : 'opacity-0'
                )}
                style={overlayTopStyle}
                onClick={handleBackdropClick}
            />
            <div
                ref={dialogRef}
                id={EATALY_FULLSCREEN_NAV_OVERLAY_ID}
                role="dialog"
                aria-modal="true"
                aria-label={t('navigationMenu')}
                tabIndex={-1}
                data-testid="eataly-fullscreen-nav"
                className="pointer-events-none fixed inset-x-0 bottom-0 z-100 flex items-start outline-none"
                style={overlayTopStyle}
                onClick={handleDialogClick}>
                <div
                    ref={panelRef}
                    onTransitionEnd={handlePanelTransitionEnd}
                    className={cn(
                        'pointer-events-auto flex h-full w-full lg:w-fit flex-col text-foreground shadow-[0px_8px_24px_rgba(0,0,0,0.08)] md:shrink-0',
                        isEditorialMenu && 'max-w-[714px]',
                        'transform transition-transform will-change-transform bg-neutral-white md:bg-transparent',
                        OVERLAY_MOTION_TIMING,
                        isPanelVisible ? 'translate-x-0' : '-translate-x-full',
                        className
                    )}>
                    <div
                        className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto bg-neutral-white"
                        data-testid="eataly-fullscreen-nav-body">
                        {children}
                    </div>
                </div>
                <button
                    ref={closeButtonRef}
                    type="button"
                    onClick={onClose}
                    aria-label={t('close')}
                    data-testid="eataly-fullscreen-nav-close"
                    className={cn(
                        'absolute top-0 right-0 z-99 lg:static pointer-events-auto shrink-0 cursor-pointer items-center self-start p-6 text-foreground md:text-neutral-white hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2  md:focus-visible:outline-neutral-white transition-opacity',
                        OVERLAY_MOTION_TIMING,
                        isPanelVisible ? 'opacity-100' : 'opacity-0'
                    )}>
                    <SvgClose className="size-6" aria-hidden />
                </button>
            </div>
        </>,
        document.body
    );
}
